import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import Util from '../../../../forecast-app/shared/util/util';
import {HighLevelBudgetNumbersContainer, HighLevelBudgetNumbersBox} from './HighLevelBudgetNumbers.styled';
import {getCurrency, NUMBER_TYPE} from './util/BudgetUtils';
import {BUDGET_TYPE, PERIOD_BUDGET_TYPE} from '../../../../constants';
import {offsetTooltip} from './util/BudgetTooltips';
import {createFragmentContainer, graphql} from 'react-relay';
import {hasFeatureFlag} from '../../../../forecast-app/shared/util/FeatureUtil';
import {HighLevelNumber} from './HighLevelNumber';

const HighLevelBudgetNumbers = ({viewer}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {company, project} = viewer;
	const hasLimitedFinancialAccess = Util.hasLimitedFinancialAccess();
	const hasRevenueRecognition = hasFeatureFlag('revenue_recognition');
	const {
		budget,
		budgetType,
		defaultPeriodBudgetType,
		fixedPriceLocks,
		financialNumbers,
		workFinancialNumbers,
		expenseFinancialNumbers,
		valueBasedProgress,
	} = project;
	const {
		actualRevenue,
		actualCost,
		remainingCost,
		forecastCost,
		invoiced,
		paid,
		budgetVsTotalRateValue,
		totalRateValue,
		remainingRevenue,
		forecastRevenue,
		recognitionProfit,
		recognitionProfitPercentage,
	} = financialNumbers;
	const {actualRevenue: workActualRevenue} = workFinancialNumbers;
	const {actualRevenue: expenseActualRevenue} = expenseFinancialNumbers;

	const isUsingLocks = fixedPriceLocks.edges.length > 0;
	const {
		actualRevenueFromWorkInLockedPeriod: lockedWorkTotal,
		actualRevenueFromExpensesInLockedPeriod: lockedExpenseTotal,
		totalOffsetInLockedPeriod: lockedOffsetTotal,
	} = project;

	const currency = getCurrency(company, project);
	const currencySymbol = Util.GetCurrencySymbol(currency);
	const isFixedPrice =
		budgetType === BUDGET_TYPE.FIXED_PRICE ||
		budgetType === BUDGET_TYPE.FIXED_PRICE_V2 ||
		(budgetType === BUDGET_TYPE.RETAINER && defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE);
	const isFixedPriceV2 = budgetType === BUDGET_TYPE.FIXED_PRICE_V2;

	const budgetVsTotalRateValueRow = budgetVsTotalRateValue !== null && (
		<HighLevelNumber
			currencySymbol={currencySymbol}
			labelTranslationId={'project_budget.budgetVsTotalValue.shortTitle'}
			value={budgetVsTotalRateValue}
			numberType={NUMBER_TYPE.MONEY}
			cy={'budget-vs-total-value'}
			tooltipItems={[
				{
					title: formatMessage({id: 'project_budget.budgetVsTotalValue.longTitle'}),
				},
				{
					description:
						budgetVsTotalRateValue > 0 ? (
							<FormattedMessage
								id="project_budget.budgetVsTotalValue.budgetHigherDescription"
								values={{
									highlight: chunk => <span style={{fontWeight: 'bold', color: '#00ac51'}}>{chunk}</span>,
								}}
							/>
						) : (
							<FormattedMessage
								id="project_budget.budgetVsTotalValue.budgetLowerDescription"
								values={{
									highlight: chunk => <span style={{fontWeight: 'bold', color: '#fd3027'}}>{chunk}</span>,
								}}
							/>
						),
				},
				{
					details: [
						formatMessage({id: 'common.fixed_price'}),
						'=',
						Util.getFormattedNumberWithCurrency(currencySymbol, budget, intl),
					],
				},
				{
					details: [
						formatMessage({id: 'project_budget.budgetVsTotalValue.totalValue'}),
						'=',
						Util.getFormattedNumberWithCurrency(currencySymbol, totalRateValue, intl),
					],
				},
			]}
		/>
	);

	return (
		<>
			{hasRevenueRecognition && isFixedPriceV2 ? (
				<HighLevelBudgetNumbersContainer>
					<HighLevelBudgetNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.fixed_price_budget'}
							value={budget}
							numberType={NUMBER_TYPE.MONEY}
							cy={'fixed-price-budget'}
							tooltipItems={[]}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.actual_spend_to_date'}
							value={actualRevenue}
							numberType={NUMBER_TYPE.MONEY}
							cy={'actual-spend-to-date'}
							tooltipItems={[]}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.budget_remaining'}
							value={budget - actualRevenue}
							numberType={NUMBER_TYPE.MONEY}
							cy={'budget-remaining'}
							tooltipItems={[]}
							bold
						/>
					</HighLevelBudgetNumbersBox>
					<HighLevelBudgetNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.high_level_numbers.forecast_to_complete'}
							value={remainingRevenue}
							numberType={NUMBER_TYPE.MONEY}
							cy={'remaining-revenue'}
							tooltipItems={[]}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.high_level_numbers.total_at_completion'}
							value={forecastRevenue}
							numberType={NUMBER_TYPE.MONEY}
							cy={'forecast-revenue'}
							tooltipItems={[]}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.variance_forecast_budget'}
							value={budget - forecastRevenue}
							numberType={NUMBER_TYPE.MONEY}
							cy={'variance-forecast-budget'}
							tooltipItems={[]}
							bold
						/>
					</HighLevelBudgetNumbersBox>
					<HighLevelBudgetNumbersBox>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.actual_cost_to_date'}
							value={actualCost}
							numberType={NUMBER_TYPE.MONEY}
							cy={'actual-cost-to-date'}
							tooltipItems={[]}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.high_level_numbers.forecast_cost_to_complete'}
							value={remainingCost}
							numberType={NUMBER_TYPE.MONEY}
							cy={'remaining-cost'}
							tooltipItems={[]}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.total_cost_at_completion'}
							value={forecastCost}
							numberType={NUMBER_TYPE.MONEY}
							cy={'forecast-cost'}
							tooltipItems={[]}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.high_level_numbers.forecast_profit_at_completion'}
							value={recognitionProfit}
							numberType={NUMBER_TYPE.MONEY}
							cy={'forecast-profit'}
							tooltipItems={[]}
						/>
						<HighLevelNumber
							currencySymbol={currencySymbol}
							labelTranslationId={'project_budget.high_level_numbers.forecast_profit_at_completion_percent'}
							value={recognitionProfitPercentage}
							numberType={NUMBER_TYPE.PERCENTAGE}
							cy={'forecast-profit-percentage'}
							tooltipItems={[]}
						/>
					</HighLevelBudgetNumbersBox>
				</HighLevelBudgetNumbersContainer>
			) : (
				<HighLevelBudgetNumbersContainer>
					<HighLevelBudgetNumbersBox>
						{isFixedPrice && (!isFixedPriceV2 || !isUsingLocks) ? (
							<>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.fixed_price_revenue_brackets'}
									value={budget}
									numberType={NUMBER_TYPE.MONEY}
									cy={'fixed-price-revenue'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.actual_revenue'}
									value={actualRevenue}
									numberType={NUMBER_TYPE.MONEY}
									cy={'actual-revenue'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.remaining_revenue'}
									value={remainingRevenue}
									numberType={NUMBER_TYPE.MONEY}
									cy={'remaining-revenue'}
									tooltipItems={[]}
								/>
								{!hasLimitedFinancialAccess && (
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'common.cost'}
										value={forecastCost}
										numberType={NUMBER_TYPE.MONEY}
										cy={'cost'}
										tooltipItems={[]}
									/>
								)}
								{!hasLimitedFinancialAccess && (
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'common.profit'}
										value={budget - forecastCost}
										numberType={NUMBER_TYPE.MONEY}
										cy={'profit'}
										tooltipItems={[]}
									/>
								)}
								{isFixedPriceV2 && totalRateValue ? (
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'project_budget.budgetVsTotalValue.totalValue'}
										value={totalRateValue}
										numberType={NUMBER_TYPE.MONEY}
										cy={'budget-vs-rate'}
										tooltipItems={[]}
									/>
								) : null}
								{isFixedPriceV2 ? budgetVsTotalRateValueRow : null}
							</>
						) : isFixedPriceV2 ? (
							<>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'common.progress'}
									value={valueBasedProgress}
									numberType={NUMBER_TYPE.PERCENTAGE}
									cy={'progress'}
									tooltipItems={[
										{
											title: formatMessage({id: 'common.progress'}),
											details: [
												formatMessage({id: 'project_budget.done_task_value'}),
												'/',
												formatMessage({id: 'project_budget.total_task_value'}),
											],
										},
									]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.closed_billable_work'}
									value={lockedWorkTotal}
									numberType={NUMBER_TYPE.MONEY}
									cy={'closed-billable-work'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.closed_billable_expenses'}
									value={lockedExpenseTotal}
									numberType={NUMBER_TYPE.MONEY}
									cy={'closed-billable-expenses'}
									tooltipItems={[]}
								/>
								{+lockedOffsetTotal !== 0 && (
									<HighLevelNumber
										currencySymbol={currencySymbol}
										labelTranslationId={'project_budget.total_offset'}
										value={lockedOffsetTotal}
										numberType={NUMBER_TYPE.MONEY}
										cy={'total-offset'}
										tooltipItems={offsetTooltip(formatMessage)}
									/>
								)}
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.invoiced'}
									value={invoiced}
									numberType={NUMBER_TYPE.MONEY}
									cy={'invoiced'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.invoice_paid'}
									value={paid}
									numberType={NUMBER_TYPE.MONEY}
									cy={'invoice-paid'}
									tooltipItems={[]}
								/>
								{budgetVsTotalRateValueRow}
							</>
						) : (
							<>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.billable_hours'}
									value={workActualRevenue}
									numberType={NUMBER_TYPE.MONEY}
									cy={'billable-hours'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'expenses.billable_expenses'}
									value={expenseActualRevenue}
									numberType={NUMBER_TYPE.MONEY}
									cy={'billable-expenses'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.invoiceable_total'}
									value={actualRevenue}
									numberType={NUMBER_TYPE.MONEY}
									cy={'invoiceable-total'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.invoiced'}
									value={invoiced}
									numberType={NUMBER_TYPE.MONEY}
									cy={'invoice'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.uninvoiced'}
									value={actualRevenue - invoiced}
									numberType={NUMBER_TYPE.MONEY}
									cy={'uninvoiced'}
									tooltipItems={[]}
								/>
								<HighLevelNumber
									currencySymbol={currencySymbol}
									labelTranslationId={'project_budget.invoice_paid'}
									value={paid}
									numberType={NUMBER_TYPE.MONEY}
									cy={'invoice-paid'}
									tooltipItems={[]}
								/>
							</>
						)}
					</HighLevelBudgetNumbersBox>
				</HighLevelBudgetNumbersContainer>
			)}
		</>
	);
};

const highLevelBudgetNumbersQuery = graphql`
	query HighLevelBudgetNumbers_Query($projectId: ID!) {
		viewer {
			id
			actualPersonId
			userType
			component(name: "high_level_numbers_loader")
			...HighLevelBudgetNumbers_viewer @arguments(projectId: $projectId)
		}
	}
`;

export {highLevelBudgetNumbersQuery};

export default createFragmentContainer(HighLevelBudgetNumbers, {
	viewer: graphql`
		fragment HighLevelBudgetNumbers_viewer on Viewer @argumentDefinitions(projectId: {type: "ID!"}) {
			id
			company {
				currency
			}
			project(internalId: $projectId) {
				id
				budget
				budgetType
				defaultPeriodBudgetType
				valueBasedProgress
				timeRegBasedPlusDoneScopeCompletion
				actualRevenueFromWorkInLockedPeriod
				actualRevenueFromExpensesInLockedPeriod
				totalOffsetInLockedPeriod
				rateCard {
					currency
				}
				fixedPriceLocks(first: 1000) @connection(key: "Project_fixedPriceLocks", filters: []) {
					edges {
						node {
							id
							startDate
							endDate
							actualRevenueProjectCurrency
							progress
						}
					}
				}
				financialNumbers(convertToProjectCurrency: true) {
					actualRevenue
					actualCost
					remainingRevenue
					remainingCost
					forecastCost
					forecastRevenue
					invoiced
					paid
					totalRateValue
					budgetVsTotalRateValue
					recognitionProfit
					recognitionProfitPercentage
				}
				workFinancialNumbers: financialNumbers(
					searchQuery: {filters: [{field: ROLE, operator: IS_NOT, value: "Um9sZTotMQ=="}]}
					convertToProjectCurrency: true
				) {
					actualRevenue
				}
				expenseFinancialNumbers: financialNumbers(
					searchQuery: {filters: [{field: ROLE, operator: IS, value: "Um9sZTotMQ=="}]}
					convertToProjectCurrency: true
				) {
					actualRevenue
				}
			}
		}
	`,
});
